import React, { useState, useRef, useEffect } from "react";
import styles from "./tools-modal.module.css";
import { MyIcons } from "../../icons/my-icons";
import { useModal } from "../../../contexts/ModalContext";

const ToolsModal = ({ isOpen, onClose, position, menuItems }) => {
  const menuRef = useRef();
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [activeSubSubmenu, setActiveSubSubmenu] = useState(null);
  const { openModal } = useModal();
  const userRole = localStorage.getItem("userRole") || "ROLE_USER";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleMouseEnter = (itemLabel, isSubSubmenu = false) => {
    if (isSubSubmenu) {
      setActiveSubSubmenu(itemLabel);
    } else {
      setActiveSubmenu(itemLabel);
      setActiveSubSubmenu(null);
    }
  };

  const handleItemClick = (item) => {
    // Check if it's a lista item (has listType)
    if (item.listType) {
      openModal("ListasModal", { listType: item.listType });
      onClose();
      return;
    }

    if (item.label === "Contactos") {
      openModal("ContactsModal");
    }
    if (item.label === "Operarios") {
      openModal("OperatorsModal");
    }
    if (item.label === "Compañías") {
      openModal("CompanyModal");
    }
    if (item.label === "Presupuestos predeterminados") {
      openModal("PresetBudgetModal");
    }
    if (item.label === "Notas de presupuesto") {
      openModal("BudgetNotesModal");
    }
    if (item.label === "Comerciales") {
      openModal("CommercialsModal");
    }
    if (item.label === "Gremios") {
      openModal("GuildsModal");
    }
    if (item.label === "Marcadores de corte Gmail") {
      openModal("GmailCutMarkersModal");
    }
    if (item.label === "Estados") {
      openModal("StatesModal");
    }
    if (item.label === "Tipos de trabajo") {
      openModal("JobTypeModal");
    }
    if (item.label === "Localidades") {
      openModal("LocalityModal");
    }
    if (item.label === "Ver documentos") {
      openModal("VerifactuModal");
    }
    if (item.label === "Dar por Cobrado") {
      openModal("DarCobradoModal");
    }
    if (item.label === "Honorarios Pagados") {
      openModal("HonorariosPagadosModal");
    }
    if (item.action) {
      item.action();
      onClose();
    }
  };

  // Check if user has required role
  const hasRequiredRole = (item) => {
    if (!item.roleRequired) return true;
    return item.roleRequired.includes(userRole);
  };

  // Render sub-submenu (third level)
  const renderSubSubmenu = (parentLabel, subItem) => {
    if (!subItem.hasSubmenu || !subItem.submenu) return null;

    const submenuClasses = [
      styles.subSubmenuContainer,
      styles.openRight
    ].filter(Boolean).join(' ');

    return (
      <div 
        className={submenuClasses}
        onMouseEnter={() => setActiveSubSubmenu(subItem.label)}
      >
        <div className={styles.submenu}>
          {subItem.submenu
            .filter(hasRequiredRole)
            .map((subSubItem, index) => (
              <div
                key={`subsubmenu-${parentLabel}-${subItem.label}-${index}`}
                className={styles.menuItem}
                onClick={() => handleItemClick(subSubItem)}
              >
                <span className={styles.menuItemText}>{subSubItem.label}</span>
              </div>
            ))}
        </div>
      </div>
    );
  };

  const renderSubmenu = (itemLabel) => {
    const parentItem = menuItems.find((item) => item.label === itemLabel);
    if (!parentItem || !parentItem.submenu) return null;

    // Calcular posicionamiento del submenú
    const menuRect = menuRef.current?.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const submenuWidth = 250;
    
    let shouldOpenLeft = false;
    let shouldOpenUp = false;
    
    if (menuRect) {
      // Verificar si se sale por la derecha
      shouldOpenLeft = menuRect.right + submenuWidth > windowWidth;
      
      // Para el submenú de Tablas, verificar si debe abrirse hacia arriba
      if (itemLabel === "Tablas" || itemLabel === "Listas") {
        const submenuEstimatedHeight = parentItem.submenu.length * 40;
        const spaceBelow = windowHeight - menuRect.top;
        shouldOpenUp = spaceBelow < submenuEstimatedHeight;
      }
    }

    const submenuClasses = [
      styles.submenuContainer,
      shouldOpenLeft ? styles.openLeft : '',
      shouldOpenUp ? styles.openUp : ''
    ].filter(Boolean).join(' ');

    return (
      <div 
        className={submenuClasses}
        onMouseEnter={() => setActiveSubmenu(itemLabel)}
      >
        <div className={styles.submenu}>
          {parentItem.submenu
            .filter(hasRequiredRole)
            .map((subItem, index) => (
              <div
                key={`submenu-${itemLabel}-${index}`}
                className={styles.menuItem}
                onMouseEnter={() => handleMouseEnter(subItem.label, true)}
                onMouseLeave={() => !subItem.hasSubmenu && setActiveSubSubmenu(null)}
                onClick={() => !subItem.hasSubmenu && handleItemClick(subItem)}
              >
                <span className={styles.menuItemText}>{subItem.label}</span>
                {subItem.hasSubmenu && (
                  <>
                    <MyIcons
                      name="arrowRight"
                      width={18}
                      height={18}
                      color="var(--black)"
                    />
                    {activeSubSubmenu === subItem.label && renderSubSubmenu(itemLabel, subItem)}
                  </>
                )}
              </div>
            ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className={styles.contextMenuContainer}
        style={{ top: position?.top, left: position?.left }}
        ref={menuRef}
      >
        <div className={styles.menu}>
          {menuItems.map((item, index) => (
            <div
              key={`menu-item-${index}`}
              className={styles.menuItem}
              data-menu-item={item.label}
              onMouseEnter={() => handleMouseEnter(item.label)}
              onMouseLeave={() => !item.hasSubmenu && setActiveSubmenu(null)}
              onClick={() => !item.hasSubmenu && handleItemClick(item)}
            >
              {item.icon && (
                <MyIcons
                  name={item.icon}
                  width={18}
                  height={18}
                  color="var(--black)"
                  className={styles.menuItemIcon}
                />
              )}
              <span className={styles.menuItemText}>{item.label}</span>
              {item.hasSubmenu && (
                <MyIcons
                  name="arrowRight"
                  width={18}
                  height={18}
                  color="var(--black)"
                  className={styles.submenuIcon}
                />
              )}
              {activeSubmenu === item.label &&
                item.hasSubmenu &&
                renderSubmenu(item.label)}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default ToolsModal;
